import React, { forwardRef, useEffect, useState } from 'react';

import { SmIcon, SmButton } from '@smiles/smiles-react-design-system';
import Mustache from 'mustache';

import { useDebounce } from '@/hooks/useDebounce';
import usePurchase from '@/hooks/usePurchaseData';
import useEvent from '@/hooks/useEvent';
import useApi from '@/hooks/useApi';

import { dispatchInteraction } from '@/services/requests/Interactions/helpers';

import { numberToBrl } from '@/helpers/StringHelper/numberToBrl';
import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';

import { ICounter } from './types';

export type { ICounter };

export const Counter = forwardRef<HTMLInputElement, ICounter>(
  (
    {
      id,
      min = 1000,
      max = 10000,
      step = 1000,
      label,
      labelIcon,
      maxLabel,
      minLabel,
      increaseIcon = <SmIcon name="add" />,
      increaseIconTitle = 'Aumentar valor',
      decreaseIcon = <SmIcon name="remove" />,
      decreaseIconTitle = 'Diminuir valor',
      liferayContent,
      ...props
    },
    ref,
  ) => {
    const [value, setValue] = useState(props.value || 1000);
    const debouncedValue = useDebounce(Number(value), 500);
    const {
      getPurchaseValues,
      setMilesToPurchase,
      getListQuantityMilesToPurchase,
    } = usePurchase();
    const { paymentMethodText } = liferayContent;

    const { total, installments, installmentValue } =
      getPurchaseValues(debouncedValue);

    const totalAmmount = numberToBrl(total);

    const isNumber = (text: string) => {
      return /^-?\d+$/g.test(text);
    };

    const { ApiInteractions } = useApi();
    const event = useEvent();

    useEffect(() => {
      setMilesToPurchase(Number(value));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleValueUpdate = (operation: string) => {
      const currentValue = isNumber(value as string) ? Number(value) : value;
      if (typeof currentValue === 'number') {
        if (operation === 'sub') {
          let newValue =
            getListQuantityMilesToPurchase()
              .reverse()
              .find(item => item <= currentValue - step) || currentValue;
          if (currentValue < min || newValue < min) newValue = min;
          else if (currentValue > max) newValue = max;
          setValue(newValue);
          setMilesToPurchase(newValue);
          dispatchInteraction(
            ApiInteractions,
            'extra_mpv_value_select_miles_value',
            {
              recommendationId: event.data.recommendationId,
              details: { miles: newValue },
            },
          );
        } else if (operation === 'add') {
          let newValue =
            getListQuantityMilesToPurchase().find(
              item => item >= currentValue + step,
            ) || currentValue;
          if (currentValue > max || newValue > max) newValue = max;
          else if (currentValue < min) newValue = min;
          setValue(newValue);
          setMilesToPurchase(newValue);
          dispatchInteraction(
            ApiInteractions,
            'extra_mpv_value_select_miles_value',
            {
              recommendationId: event.data.recommendationId,
              details: { miles: newValue },
            },
          );
        }
      }
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = +e.target.value.replace(/[^0-9.]/, '') * step;

      newValue =
        newValue === 0
          ? newValue
          : getListQuantityMilesToPurchase().find(item => item >= newValue) ||
            newValue;

      if (newValue > max) {
        newValue = max;
      }
      setValue(newValue);
      setMilesToPurchase(newValue);
    };

    return (
      <div className="smls-b2c-modals__modal__counter">
        <label className="smls-b2c-modals__modal__counter--label" htmlFor={id}>
          {label}
          {labelIcon && (
            <SmIcon name={labelIcon} type="outlined" size="small" />
          )}
        </label>
        <div className="smls-b2c-modals__modal__counter--content">
          <SmButton
            className="smls-b2c-modals__modal__counter--content--button"
            type="button"
            icon={decreaseIcon}
            disabled={props.disabled || value === min}
            isUnstyled
            title={decreaseIconTitle}
            onClick={() => {
              handleValueUpdate('sub');
            }}
            aria-label={decreaseIconTitle}
          />
          <div className="smls-b2c-modals__modal__counter--content--group">
            <input
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
              className="smls-b2c-modals__modal__counter--content--input"
              id={id}
              ref={ref}
              type="text"
              min={min}
              max={max.toString()}
              value={(+value / step)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
              pattern="^[0-9]"
              disabled={props.disabled}
              maxLength={6}
              onChange={handleNumberChange}
              aria-label={label}
            />
            <span>.000</span>
          </div>
          <SmButton
            className="smls-b2c-modals__modal__counter--content--button"
            icon={increaseIcon}
            type="button"
            disabled={props.disabled || value === max}
            isUnstyled
            title={increaseIconTitle}
            onClick={() => {
              handleValueUpdate('add');
            }}
            aria-label={increaseIconTitle}
          />
        </div>
        <small
          id={`${id}-description`}
          className="c-field__description"
          data-testid="test-icon-container"
        >
          <SmIcon
            type="outlined"
            name="info"
            size="small"
            data-testid="test-success-icon"
          />
          {minLabel}{' '}
          <strong>
            {min.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
          </strong>{' '}
          | {maxLabel}{' '}
          <strong>
            {max.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
          </strong>
        </small>

        <div className="smls-b2c-modals__modal--calculator_information">
          {paymentMethodText && (
            <span className="smls-b2c-modals__modal--calculator_information__paymentmethod">
              {clearHtmlTags(
                Mustache.render(paymentMethodText.text, {
                  totalAmmount,
                  installments,
                  installmentValue: numberToBrl(installmentValue),
                }),
              )}
            </span>
          )}
        </div>
      </div>
    );
  },
);
